import { useState, useEffect, useCallback } from 'react';
import request from '@/utils/request';

interface UserRecord {
  id: number;
  avatarUrl?: string;
  nickname?: string;
  openId?: string;
  coinBalance?: number;
  lastLogin?: string;
  createdAt?: string;
}

interface Pagination {
  page: number;
  pageSize: number;
  total: number;
}

interface UseUsersReturn {
  users: UserRecord[];
  loading: boolean;
  pagination: Pagination;
  search: string;
  setSearch: (value: string) => void;
  setPage: (page: number, pageSize?: number) => void;
  refresh: () => void;
}

export function useUsers(): UseUsersReturn {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearchValue] = useState('');
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: 20,
    total: 0,
  });

  const fetchUsers = useCallback(
    async (page: number, pageSize: number, searchTerm: string) => {
      setLoading(true);
      try {
        const { data } = await request.get('/admin/users', {
          params: {
            page,
            pageSize,
            search: searchTerm || undefined,
          },
        });

        const list = Array.isArray(data) ? data : data?.items ?? [];
        const total = data?.total ?? list.length;

        setUsers(list);
        setPagination({ page, pageSize, total });
      } catch {
        setUsers([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchUsers(pagination.page, pagination.pageSize, search);
    // Only re-fetch when search changes - reset to page 1
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Initial fetch
  useEffect(() => {
    fetchUsers(1, pagination.pageSize, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSearch = useCallback((value: string) => {
    setSearchValue(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const setPage = useCallback(
    (page: number, pageSize?: number) => {
      const newPageSize = pageSize ?? pagination.pageSize;
      setPagination((prev) => ({ ...prev, page, pageSize: newPageSize }));
      fetchUsers(page, newPageSize, search);
    },
    [fetchUsers, pagination.pageSize, search],
  );

  const refresh = useCallback(() => {
    fetchUsers(pagination.page, pagination.pageSize, search);
  }, [fetchUsers, pagination.page, pagination.pageSize, search]);

  return {
    users,
    loading,
    pagination,
    search,
    setSearch,
    setPage,
    refresh,
  };
}
